import React, { useState, useEffect, useContext, useCallback } from "react";
import axios from "axios";
import Loader from "../../components/Global/Loader";
import { UserContext } from "../../context/UserContext";
import PersonalInfoLeftCard from "../../components/Student/Personal Information/PersonalInfoLeftCard";
import PersonalInfoForm from "../../components/Student/Personal Information/PersonalInfoForm";
import AddFirstEducation from "../../components/Student/Personal Information/AddFirstEducation";
import EducationDetails from "../../components/Student/Personal Information/EducationDetails";
import AddEducationForm from "../../components/Student/Personal Information/AddEducationForm";
import ViewAndUpdateEducation from "../../components/Student/Personal Information/ViewAndUpdateEducation";
import AddressInfoForm from "../../components/Student/Personal Information/AddressInfoForm";
import ChangePassword from "../../components/Student/Personal Information/ChangePassword";
import ChangeEmail from "../../components/Student/Personal Information/ChangeEmail";
import ChangeMobileNumber from "../../components/Student/Personal Information/ChangeMobileNumber";
import StudentDocuments from "../../components/Student/Personal Information/StudentDocuments";

const INITIAL_FORM_STATE = {
  profilePicture: "",
  profileURL: "",
  title: "",
  firstName: "",
  lastName: "",
  maritalStatus: "",
  gender: "",
  passportNumber: "",
  dateOfBirth: "",
  countryOfBirth: "",
  religion: "",
  domicileProvince: "",
  domicileCity: "",
  domicileDistrict: "",
  fatherName: "",
  fatherStatus: "",
  fatherCNIC: "",
  cnic: "",
  nationality: "",
  address: "",
  addressCity: "",
  addressCountry: "",
  educationDetails: [],
  cellPhone: "",
  email: "",
};

const mapApiDataToFormState = (data = {}) => ({
  ...INITIAL_FORM_STATE,
  ...data,
  profilePicture: "",
  profileURL: data?.profileURL || "",
  educationDetails: Array.isArray(data?.educationDetails)
    ? data.educationDetails
    : [],
});

const PersonalInformation = () => {
  const [updateToggle, setUpdateToggle] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [apiData, setApiData] = useState(() => mapApiDataToFormState());

  // ----- USER CONTEXT -----
  const { setName, setPrimaryEmail, setProfileUrl, setIsEligible } =
    useContext(UserContext);

  // ----- PAGE STATE -----
  const [active, setActive] = useState("personal-information");
  const [activeEducationPage, setActiveEducationPage] = useState("");
  const [educationRecordAction, setEducationRecordAction] = useState("");

  const [selectedEducation, setSelectedEducation] = useState(null);

  const [formData, setFormData] = useState(() => mapApiDataToFormState());

  const clearUnsavedData = useCallback(() => {
    setFormData(mapApiDataToFormState(apiData));
    setSelectedImage(null);
    if (previewUrl) {
      try {
        URL.revokeObjectURL(previewUrl);
      } catch (error) {
        // Ignore revoke errors; the URL may already be released.
      }
    }
    setPreviewUrl(null);
  }, [apiData, previewUrl]);

  // ---------------------------
  // 1) LOAD USER DATA ON MOUNT
  // ---------------------------
  const getUserDetails = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/student`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.status === 200) {
        setIsEligible(response.data.data.isEligible);
        const data = response.data.data;

        // Format dateOfBirth if present
        if (data.dateOfBirth) {
          data.dateOfBirth = new Date(data.dateOfBirth)
            .toISOString()
            .split("T")[0];
        }

        const sanitizedData = mapApiDataToFormState(data);
        setApiData(sanitizedData);
        setFormData(mapApiDataToFormState(data));
        // setUpdateToggle((prev) => !prev);

        // Update context values only if they exist in the response
        if (data.firstName && data.lastName)
          setName(`${data.firstName} ${data.lastName}`);
        if (data.email) setPrimaryEmail(data.email);
        if (data.profileURL) setProfileUrl(data.profileURL);
      }
    } catch (error) {
      // console.log(error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getUserDetails();
  }, []);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // ---------------------------------
  // 2) HISTORY POPSTATE EVENT LISTENER
  // ---------------------------------
  useEffect(() => {
    // Ensure initial state is stored
    if (!window.history.state) {
      window.history.replaceState(
        {
          active: "personal-information",
          activeEducationPage: "",
          educationRecordAction: "",
        },
        "",
        ""
      );
    }

    const onPopState = (event) => {
      const nextActive = event.state?.active || "personal-information";
      const nextEducationPage = event.state?.activeEducationPage || "";
      const nextEduAction = event.state?.educationRecordAction || "";

      if (nextActive !== active) {
        clearUnsavedData();
      }

      setActive(nextActive);
      setActiveEducationPage(nextEducationPage);
      setEducationRecordAction(nextEduAction);
    };

    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [active, clearUnsavedData]);

  // --------------------------------------------------
  // 3) HELPER TO PUSH A NEW STATE TO BROWSER HISTORY
  // --------------------------------------------------
  const navigateState = (
    newActive,
    newActiveEducationPage = "",
    newEduAction = ""
  ) => {
    if (newActive !== active) {
      clearUnsavedData();
    }

    // Construct the new history state object
    const stateObj = {
      active: newActive,
      activeEducationPage: newActiveEducationPage,
      educationRecordAction: newEduAction,
    };

    // Push to browser history (no actual URL change)
    window.history.pushState(stateObj, "", "");

    // Update React states
    setActive(newActive);
    setActiveEducationPage(newActiveEducationPage);
    setEducationRecordAction(newEduAction);
  };

  // Example usage: navigateState("education-details", "add-education", "");

  // --------------------------------------
  // 4) REPLACE DIRECT SETTERS WITH NAVIGATE
  // --------------------------------------
  // For example, your handleImageChange remains the same
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    const maxSize = 2 * 1024 * 1024; // 2MB

    if (file.size > maxSize) {
      alert("File size should not exceed 2MB");
      return;
    }

    if (file && ["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
      setSelectedImage(file);
      setFormData((prev) => ({ ...prev, profilePicture: file }));

      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
    } else {
      alert("Please select a valid image file (JPEG, PNG, or JPG)");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    // console.log(formData);

    // Build data object
    const dataToSubmit = {};
    Object.entries(formData).forEach(([key, value]) => {
      if (key === "profileURL" && typeof value === "string") return;
      if (value !== null && value !== undefined) {
        dataToSubmit[key] = value;
      }
    });
    // console.log("Form Data Entries:", dataToSubmit);

    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_BASE_URL}/student`,
        dataToSubmit,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
        getUserDetails();
        setUpdateToggle((prev) => !prev);
    } catch (error) {
      // console.log(error);
      alert(
        error?.response?.data?.message ||
          "An error occurred while updating data"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // console.log("Form closed");
    // ...
  };

  // Called by <EducationDetails /> when user wants to view or edit
  const handleViewOrEditEducation = (edu, action) => {
    setSelectedEducation(edu);
    navigateState("education-details", "view-update", action);
  };

  return (
    <div className="relative pl-36 mt-16 w-full p-8 bg-[#f4f8fb]">
      {isLoading && <Loader />}

      <div className="flex flex-wrap space-x-6">
        <PersonalInfoLeftCard
          formData={apiData}
          updateToggle={updateToggle}
          active={active}
          // Instead of setActive
          onNavigate={(newActive) => navigateState(newActive)}
        />

        {active === "personal-information" ? (
          <PersonalInfoForm
            formData={formData}
            previewUrl={previewUrl}
            handleImageChange={handleImageChange}
            handleChange={handleChange}
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            getUserDetails={getUserDetails}
            isLoading={isLoading}
          />
        ) : active === "documents" ? (
          <StudentDocuments />
        ) : active === "education-details" ? (
          formData.educationDetails &&
          formData.educationDetails.length > 0 &&
          activeEducationPage === "" ? (
            <EducationDetails
              educationData={formData.educationDetails}
              setActiveEducationPage={(val) =>
                navigateState("education-details", val)
              }
              getUserDetails={getUserDetails}
              onViewOrEdit={handleViewOrEditEducation}
            />
          ) : activeEducationPage === "add-education" ? (
            <AddEducationForm
              getUserDetails={getUserDetails}
              setActiveEducationPage={(val) =>
                navigateState("education-details", val)
              }
            />
          ) : activeEducationPage === "view-update" ? (
            <ViewAndUpdateEducation
              educationData={selectedEducation}
              setActiveEducationPage={(val) =>
                navigateState("education-details", val)
              }
              educationRecordAction={educationRecordAction}
              setEducationRecordAction={setEducationRecordAction}
              getUserDetails={getUserDetails}
            />
          ) : (
            <AddFirstEducation
              setActiveEducationPage={(val) =>
                navigateState("education-details", val)
              }
            />
          )
        ) : active === "address-information" ? (
          <AddressInfoForm
            formData={formData}
            handleClose={handleClose}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isLoading={isLoading}
          />
        ) : active === "change-password" ? (
          <ChangePassword />
        ) : active === "change-email" ? (
          <ChangeEmail />
        ) : active === "change-mobile-number" ? (
          <ChangeMobileNumber setFormData={setApiData} />
        ) : (
          <PersonalInfoForm // fallback case if any case does not match
            formData={formData}
            previewUrl={previewUrl}
            handleImageChange={handleImageChange}
            handleChange={handleChange}
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            getUserDetails={getUserDetails}
            isLoading={isLoading}
          />
        )}
      </div>
    </div>
  );
};

export default PersonalInformation;
